'use client'

import { memo } from 'react'
import type { Task } from './KanbanBoard'

// ============================================================================
// Types
// ============================================================================

interface KanbanCardProps {
  task: Task
  onDragStart: (taskId: string) => void
  onDragEnd: () => void
  onClick?: (taskId: string) => void
}

// ============================================================================
// Component
// ============================================================================

export const KanbanCard = memo(function KanbanCard({
  task,
  onDragStart,
  onDragEnd,
  onClick
}: KanbanCardProps) {

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move'
    onDragStart(task.id)
  }

  const handleClick = () => {
    onClick?.(task.id)
  }

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const priorityColors = {
    1: 'bg-red-100 text-red-700 border-red-300',
    2: 'bg-orange-100 text-orange-700 border-orange-300',
    3: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    4: 'bg-blue-100 text-blue-700 border-blue-300',
    5: 'bg-gray-100 text-gray-700 border-gray-300'
  }

  const getPriorityColor = (priority: number) => {
    if (priority <= 2) return priorityColors[1]
    if (priority <= 4) return priorityColors[2]
    if (priority <= 6) return priorityColors[3]
    if (priority <= 8) return priorityColors[4]
    return priorityColors[5]
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 60) return `${diffMins}m atrás`
    if (diffHours < 24) return `${diffHours}h atrás`
    if (diffDays < 7) return `${diffDays}d atrás`
    return date.toLocaleDateString('pt-BR')
  }

  const calculateProgress = () => {
    if (!task.subtask_count || task.subtask_count === 0) return null

    // TODO: Get actual completed subtasks count
    // For now, estimate based on status
    if (task.status === 'done') return 100
    if (task.status === 'running') return 50
    return 0
  }

  const progress = calculateProgress()

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      onClick={handleClick}
      className="bg-white dark:bg-gray-700 rounded-lg p-3 shadow-sm hover:shadow-md transition-all cursor-move border border-gray-200 dark:border-gray-600 group"
    >
      {/* Title */}
      <h4 className="font-medium text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
        {task.titulo}
      </h4>

      {/* Metadata */}
      <div className="flex items-center gap-2 mb-2">
        {/* Priority */}
        <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getPriorityColor(task.prioridade)}`}>
          P{task.prioridade}
        </span>

        {/* Parent/Subtask indicator */}
        {task.parent_task_id ? (
          <span className="text-xs text-gray-500 dark:text-gray-400" title="Subtask">
            📎
          </span>
        ) : task.subtask_count && task.subtask_count > 0 ? (
          <span className="text-xs text-gray-500 dark:text-gray-400" title={`${task.subtask_count} subtasks`}>
            📊 {task.subtask_count}
          </span>
        ) : null}

        {/* Execution order */}
        {task.execution_order && task.execution_order > 1 && (
          <span className="text-xs text-gray-500 dark:text-gray-400" title="Execution order">
            #{task.execution_order}
          </span>
        )}
      </div>

      {/* Progress Bar (if has subtasks) */}
      {progress !== null && (
        <div className="mb-2">
          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
            <span>Progresso</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 dark:bg-blue-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span title={task.created_at}>
          {formatDate(task.created_at)}
        </span>

        {/* Task ID (short) */}
        <span className="font-mono" title={task.id}>
          {task.id.slice(0, 8)}
        </span>
      </div>
    </div>
  )
})
